/**
 * Conversions from various Vetro entities to Mapbox layers and sources and vice versa
 */
import _last from 'lodash.last';
import { v4 as uuid } from 'uuid';

import { isRasterLayer } from '../../guards';
import { isPolygon } from '../geom';
import { isCircleLayer, isPolygonOutlineMapboxLayer } from './types';

// get the source_layer_id for a _vector_ tile source
// see https://docs.mapbox.com/help/glossary/source-layer/
export const vetroLayerToSourceLayerId = (vetroLayer) => {
  const { externalVectorSourceId, externalSourceType, id } = vetroLayer;

  if (externalSourceType === 'geojson') return '';

  return externalVectorSourceId ?? `lyr_${id}`;
};

export const externalVectorSourceToVetroLayerId = (externalVectorSource) =>
  Number(_last(externalVectorSource.split('-')));

const vetroLayerToExternalSourceName = (vetroLayer) => {
  const { externalSourceType, label, id } = vetroLayer;

  if (externalSourceType) {
    return `external-${externalSourceType}-source--${label}-${id}`;
  }

  throw new Error(`Cannot get external source name for ${label} - it is not an external layer`);
};

export const vetroLayerToGeoJsonSourceName = vetroLayerToExternalSourceName;

export const vetroLayerToInternalVectorSourceName = (layer) =>
  `internal-vector-source--${layer.id}`;

export const vetroLayerToVectorSourceName = (vetroLayer) => {
  const { externalUrl } = vetroLayer;
  // internal raster layers also have a vector layer - so we dont check that the layer is vector here

  if (externalUrl) return vetroLayerToExternalSourceName(vetroLayer);

  return vetroLayerToInternalVectorSourceName(vetroLayer);
};

export const vetroLayerToPolygonCentroidSourceName = (vetroLayer) =>
  `${vetroLayerToVectorSourceName(vetroLayer)}_centroid`;

export const labelLayerSourceIdToCentroidSourceId = (sourceId) => `${sourceId}_centroid`;

export const vetroLayerToRasterSourceName = (vetroLayer) => {
  const { externalUrl, label, id } = vetroLayer;
  if (!isRasterLayer(vetroLayer)) {
    throw new Error(`Cannot get raster source name - ${label} is not a raster layer`);
  }

  if (externalUrl) return vetroLayerToExternalSourceName(vetroLayer);

  return `internal-raster-source--${label}-${id}`;
};

const getRequiredAttributes = (vetroLayer) => {
  const { labelsEnabled, labels, categorizedAttributeLabel } = vetroLayer.style;

  const requiredAttributes = [];

  if (labelsEnabled) {
    requiredAttributes.push(...labels);
  }

  if (categorizedAttributeLabel) {
    requiredAttributes.push(categorizedAttributeLabel);
  }

  return requiredAttributes;
};

const getExternalFeatureSourceUrl = (vetroLayer) => {
  const queryParams = new URLSearchParams({
    properties: getRequiredAttributes(vetroLayer),
  });

  return `${vetroLayer.externalUrl}?${queryParams}`;
};

export const getSourceUrl = ({
  tileServer,
  vetroLayer,
  offset = {
    isOffsetting: false,
    layerIds: [],
    planIds: [],
  },
  useCentroid = false,
  simplifyGeom = true,
  includeRequiredAttributes = true,
  // ensure url is unique from previous, which will (along with source.reload) force a reload of the tiles
  randomSlug = false,
}) => {
  if (vetroLayer.sourceType === 'external_feature') {
    return getExternalFeatureSourceUrl(vetroLayer);
  }

  const paramsObject = {
    layer_id: vetroLayer.id,
    mvt_name: vetroLayerToSourceLayerId(vetroLayer),
    use_centroid: useCentroid,
    is_offsetting: offset.isOffsetting,
    layer_ids: offset.layerIds,
    plan_ids: offset.planIds,
    simplify_geom: simplifyGeom,
  };

  const requiredAttributes = includeRequiredAttributes ? getRequiredAttributes(vetroLayer) : [];

  if (requiredAttributes.length > 0) {
    paramsObject.required_attributes = requiredAttributes;
  }

  if (randomSlug) {
    paramsObject.random_slug = Math.random().toString(36).substring(2, 14);
  }

  const params = new URLSearchParams(paramsObject);
  return `${tileServer}/maps/{z}/{x}/{y}.pbf?${params.toString()}`;
};

export const vetroLayerIdToMapboxLayerId = (layerId, layerIdGeomTypeMap) => {
  const geomType = layerIdGeomTypeMap[layerId]?.toLowerCase().replace('multi', '') ?? 'unknown';

  return `mapbox-layer-${layerId}-${geomType.toLowerCase()}`;
};

export const mapboxLayerIdToVetroLayerId = (mapboxLayerId) => {
  const matches = mapboxLayerId?.match(/(^mapbox-layer-)(\d+)(-\w+$)/);

  if (!matches) {
    throw new Error(
      `mapbox layer id ${mapboxLayerId} cannot be converted to vetro layer id - it is malformed.`,
    );
  }

  return Number(matches[2]);
};

export const geoJsonFeatureToVetroFeature = (feature, xVetro) => {
  return {
    geometry: feature.geometry,
    attributes: feature.properties,
    xVetro: {
      id: feature.id,
      vetroId: uuid(),
      ...xVetro,
    },
  };
};

export const vetroLayerToMapboxLayerType = (layer) => {
  if (isCircleLayer(layer)) return 'circle';
  if (isPolygonOutlineMapboxLayer(layer)) return 'line';
  const { geomType, id } = layer;
  if (isPolygon(geomType)) {
    return 'fill';
  }
  if (geomType === 'LineString') {
    return 'line';
  }
  if (geomType === 'Point') {
    return 'symbol';
  }
  throw new Error(`Cannot get mapbox type for layer with id ${id} - geomtype is ${geomType}`);
};
